import React, { useState, useRef, useEffect } from 'react';
import './ScrollableTabs.css';

function ScrollableTabs({ tabs, activeTab, onTabChange, children }) {
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);
  const scrollRef = useRef(null);

  const isRTL = () => document.documentElement.dir === 'rtl';

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      const maxScroll = scrollWidth - clientWidth;
      const isRtlMode = isRTL();
      
      if (isRtlMode) {
        // In RTL with row-reverse
        setShowRightFade(scrollLeft > 10);
        setShowLeftFade(scrollLeft < maxScroll - 10);
      } else {
        setShowLeftFade(scrollLeft > 10);
        setShowRightFade(scrollLeft < maxScroll - 10);
      }
    }
  };

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 200;
      const isRtlMode = isRTL();
      
      let scrollValue;
      if (isRtlMode) {
        // In RTL with row-reverse, reverse scroll direction
        scrollValue = direction === 'left' ? scrollAmount : -scrollAmount;
      } else {
        scrollValue = direction === 'left' ? -scrollAmount : scrollAmount;
      }
      
      scrollRef.current.scrollBy({
        left: scrollValue,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    checkScroll();
    const element = scrollRef.current;
    if (element) {
      // In RTL, scroll to end so first tab appears on right
      if (isRTL()) {
        setTimeout(() => {
          if (element) {
            element.scrollLeft = element.scrollWidth;
          }
        }, 50);
      }
      
      element.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);
      
      // Listen for direction changes
      const observer = new MutationObserver(() => {
        checkScroll();
        if (isRTL() && element) {
          setTimeout(() => {
            if (element) {
              element.scrollLeft = element.scrollWidth;
            }
          }, 50);
        }
      });
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['dir']
      });
      
      return () => {
        element.removeEventListener('scroll', checkScroll);
        window.removeEventListener('resize', checkScroll);
        observer.disconnect();
      };
    }
  }, []);

  // Find active child content
  const activeContent = React.Children.toArray(children).find(
    child => child.props.eventKey === activeTab
  );

  const isRtlMode = isRTL();
  
  return (
    <div className="scrollable-tabs-container">
      <div className="scrollable-tabs-header">
        {showLeftFade && (
          <button
            onClick={() => scroll('left')}
            className="scroll-btn scroll-btn-left"
            aria-label={isRtlMode ? "Scroll right" : "Scroll left"}
          >
            <i className={`bi ${isRtlMode ? 'bi-chevron-right' : 'bi-chevron-left'}`}></i>
          </button>
        )}
        
        {showRightFade && (
          <button
            onClick={() => scroll('right')}
            className="scroll-btn scroll-btn-right"
            aria-label={isRtlMode ? "Scroll left" : "Scroll right"}
          >
            <i className={`bi ${isRtlMode ? 'bi-chevron-left' : 'bi-chevron-right'}`}></i>
          </button>
        )}
        
        <div ref={scrollRef} className="scrollable-tabs-scroll">
          <div className="scrollable-tabs-list">
            {tabs.map((tab) => (
              <button
                key={tab.eventKey}
                onClick={() => onTabChange(tab.eventKey)}
                className={`scrollable-tab ${activeTab === tab.eventKey ? 'active' : ''}`}
                data-tab={tab.eventKey}
              >
                {tab.title}
              </button>
            ))}
          </div>
        </div>
      </div>
      
      <div className="scrollable-tabs-content">
        {React.Children.map(children, child => {
          if (!child) return null;
          const isActive = child.props.eventKey === activeTab;
          return (
            <div style={{ display: isActive ? 'block' : 'none' }}>
              {child}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Tab pane component for children
ScrollableTabs.Tab = function Tab({ children }) {
  return <>{children}</>;
};

export default ScrollableTabs;
